import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api, { formatApiError } from "../../utils/api";
import { subscribeAdminWS } from "../../lib/adminWS";
import { toast } from "../ui/sonner";
import PortalDetectionCard from "./PortalDetectionCard";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Avisos de acionamento dos portais, empilhados no canto inferior direito.
 *
 * Fica montado no layout do painel: acionamento chega sozinho e quem estiver em
 * qualquer tela precisa ver sem perder o que está fazendo. A fila é local
 * (vários chamados chegam juntos) e a decisão é do tenant — quando alguém
 * decide, o websocket avisa e o item sai da fila em todas as sessões.
 *
 * Som, sino e push no navegador vêm da Central de Notificações (evento
 * `portal.detection`, configurável em Configurações > Notificações). Aqui só
 * respeitamos o liga/desliga do evento.
 */
const EVENTO = "portal.detection";
// Quanto o aviso de "serviço aceito" fica na tela antes de sair sozinho.
const FECHAR_ACEITO_MS = 3000;
// Tempo da animação de saída (tem de casar com a `duration-300` do cartão).
const ANIMACAO_MS = 320;

export default function PortalDetectionListener() {
  const { user } = useAuth();
  const meuId = String(user?.id || user?._id || "");
  const [fila, setFila] = useState([]);
  // Radix marca o body enquanto um drawer/modal está aberto. Só vale como
  // "modal" quando existe mesmo um diálogo na tela: o Select de motivo DENTRO
  // do próprio cartão também tranca o scroll, e o cartão ficava pulando de
  // lado a cada vez que a lista de motivos abria e fechava.
  const [modalAberto, setModalAberto] = useState(false);
  useEffect(() => {
    const ler = () =>
      setModalAberto(
        document.body.hasAttribute("data-scroll-locked") &&
          !!document.querySelector('[role="dialog"],[role="alertdialog"]'),
      );
    ler();
    const obs = new MutationObserver(ler);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-scroll-locked", "style"],
      childList: true,
    });
    return () => obs.disconnect();
  }, []);

  const [motivos, setMotivos] = useState([]);
  const [salvando, setSalvando] = useState("");
  const [ligado, setLigado] = useState(true);
  // Ids com fechamento já agendado: um segundo `updated` do mesmo aceite (o
  // webhook reentrega) não pode reiniciar a contagem nem empilhar timers.
  const agendados = useRef(new Set());
  // Aceites feitos NESTA tela: esses cartões não fecham sozinhos, porque o
  // operador costuma usar o link da OS que acabou de criar.
  const aceitosAqui = useRef(new Set());
  // Quantos cartões cabem sem cortar nenhum: cada um ocupa ~300px com o
  // espaçamento, e ~60px ficam para o contador e o aviso do resto da fila.
  const [visiveis, setVisiveis] = useState(3);

  useEffect(() => {
    const calcular = () => {
      const cabem = Math.floor((window.innerHeight - 96 - 60) / 300);
      setVisiveis(Math.max(1, Math.min(3, cabem)));
    };
    calcular();
    window.addEventListener("resize", calcular);
    return () => window.removeEventListener("resize", calcular);
  }, []);

  // Desligou o evento nas configurações: nem o cartão aparece.
  useEffect(() => {
    api
      .get("/notifications/preferences/me")
      .then(({ data }) => {
        const item = (data?.effective || {})[EVENTO];
        if (item) setLigado(!!item.enabled);
      })
      .catch(() => {});
  }, []);

  // Fila inicial: websocket só entrega o que acontece depois de a tela existir.
  useEffect(() => {
    api
      .get("/portals/detections/open")
      .then(({ data }) => setFila(data?.items || []))
      .catch(() => {});
  }, []);

  // Fecha o aviso depois do tempo combinado: primeiro marca `saindo` (o cartão
  // desliza para a direita) e só então tira da fila, senão ele desapareceria
  // de uma vez e pareceria defeito.
  const fecharComAnimacao = useCallback((id, espera) => {
    if (agendados.current.has(id)) return;
    agendados.current.add(id);
    setTimeout(() => {
      setFila((f) => f.map((d) => (d.id === id ? { ...d, saindo: true } : d)));
      setTimeout(() => {
        setFila((f) => f.filter((d) => d.id !== id));
        agendados.current.delete(id);
      }, ANIMACAO_MS);
    }, espera);
  }, []);

  useEffect(() => {
    const off1 = subscribeAdminWS("portal.detection", ({ detection }) => {
      if (!detection) return;
      setFila((f) => (f.some((d) => d.id === detection.id) ? f : [...f, detection]));
    });
    const off2 = subscribeAdminWS("portal.detection.updated", ({ detection }) => {
      if (!detection) return;
      // Aceite enviado ao portal (por qualquer sessão): o cartão fica, sem
      // botões, até o webhook confirmar — aí vira "aceito" e ganha a OS.
      if (detection.awaiting_webhook && detection.status === "pending") {
        setFila((f) =>
          f.some((d) => d.id === detection.id)
            ? f.map((d) => (d.id === detection.id ? { ...d, ...detection } : d))
            : [...f, detection],
        );
        return;
      }
      // Portal respondeu que não está mais disponível para aceite: avisa e sai em 3s.
      if (detection.indisponivel_aceite) {
        setFila((f) => f.map((d) => (d.id === detection.id ? { ...d, ...detection } : d)));
        fecharComAnimacao(detection.id, FECHAR_ACEITO_MS);
        return;
      }
      // Aceito sem de-para: fica na fila até alguém escolher o tipo de serviço.
      if (detection.needs_service_type && !detection.service_id) {
        setFila((f) =>
          f.some((d) => d.id === detection.id)
            ? f.map((d) => (d.id === detection.id ? { ...d, ...detection } : d))
            : [...f, detection],
        );
        return;
      }
      // ACEITO (por um colega ou pelo webhook da automação): o aviso não
      // desaparece do nada. Vira confirmação "Serviço aceito", sem botões de
      // decisão, e sai sozinho em 3s — e quando não havia cartão (chegou só o
      // `service.accepted`, sem `service.detected` antes), ele aparece agora,
      // senão o serviço entraria sem ninguém perceber.
      if (detection.status === "accepted") {
        const local = aceitosAqui.current.has(detection.id);
        setFila((f) =>
          f.some((d) => d.id === detection.id)
            ? f.map((d) =>
                d.id === detection.id
                  ? {
                      ...d,
                      ...detection,
                      // O que esta sessão já sabe da OS não pode ser perdido.
                      service_id: d.service_id || detection.service_id,
                      service_protocolo: d.service_protocolo || detection.service_protocolo,
                      service_cancelado: d.service_cancelado ?? detection.service_cancelado,
                    }
                  : d,
              )
            : [...f, detection],
        );
        // Quem aceitou nesta tela fica com o cartão e o link da OS até
        // dispensar: ele agiu, não precisa de confirmação com prazo.
        // Quem pediu o aceite nesta tela também fica com o cartão: a OS que o
        // webhook acabou de criar aparece aqui como link.
        const pediuAqui = detection.accept_requested_by?.id && detection.accept_requested_by.id === meuId;
        if (!local && !pediuAqui) fecharComAnimacao(detection.id, FECHAR_ACEITO_MS);
        return;
      }
      // RECUSADO por um operador: igual ao aceite, o cartão não some do nada —
      // vira "Recusado por fulano" para todo mundo e sai sozinho em 3s.
      if (detection.status === "rejected" && detection.decided_by) {
        setFila((f) =>
          f.some((d) => d.id === detection.id)
            ? f.map((d) => (d.id === detection.id ? { ...d, ...detection } : d))
            : f,
        );
        fecharComAnimacao(detection.id, FECHAR_ACEITO_MS);
        return;
      }
      // Ignorado/expirado em outra tela: sai da fila sem confirmação.
      // Exceção: se o aviso desta sessão já está mostrando o link da OS
      // criada, ele fica até o operador dispensar.
      setFila((f) =>
        f.some((d) => d.id === detection.id && d.service_id)
          ? f.map((d) => (d.id === detection.id ? { ...d, ...detection, service_id: d.service_id, service_protocolo: d.service_protocolo, service_cancelado: d.service_cancelado } : d))
          : !detection.pode_decidir || detection.decided_by
            ? f.filter((d) => d.id !== detection.id)
            : f,
      );
    });
    // Dispensado por este operador em outra aba: o cartão sai só aqui.
    const off3 = subscribeAdminWS("portal.detection.hidden", ({ detection_id }) => {
      if (!detection_id) return;
      setFila((f) => f.filter((d) => d.id !== detection_id));
    });
    return () => {
      off1?.();
      off2?.();
      off3?.();
    };
  }, [fecharComAnimacao, meuId]);



  // Motivos do próprio chamado (o portal pode variar a lista por serviço).
  const carregarMotivos = useCallback(async (det) => {
    if (!det?.id) return;
    try {
      const { data } = await api.get(`/portals/detections/${det.id}/rejection-reasons`);
      setMotivos(data || []);
    } catch {
      setMotivos([]);
    }
  }, []);

  const remover = (id) => {
    // Some da fila: qualquer fechamento agendado para ele deixa de fazer
    // sentido, e o id fica livre caso o portal reoferte o chamado depois.
    agendados.current.delete(id);
    aceitosAqui.current.delete(id);
    setFila((f) => f.filter((d) => d.id !== id));
  };

  const avisarAcionamento = (r) => {
    if (!r) return;
    if (r.status === "ok") toast.success(`Acionamento aceito, OS criada e ${r.nome || "motorista"} acionado.`);
    else toast.error(`OS criada, mas não foi possível acionar ${r.nome || "o motorista"}: ${r.motivo || ""}`);
  };

  const aceitar = async (det, motorista) => {
    setSalvando(det.id);
    try {
      const { data } = await api.post(
        `/portals/detections/${det.id}/accept`,
        motorista ? { dispatch_person_id: motorista.person_id, dispatch_person_nome: motorista.nome } : {},
      );
      aceitosAqui.current.add(det.id);
      avisarAcionamento(data?.auto_dispatch);
      if (data?.needs_service_type) {
        // Aceite garantido no portal; a OS espera o tipo de serviço.
        toast.info(
          motorista
            ? "Aceito no portal. Escolha o tipo de serviço para abrir a OS e acionar o motorista."
            : "Aceito no portal. Escolha o tipo de serviço para abrir a OS.",
        );
        setFila((f) =>
          f.map((d) =>
            d.id === det.id ? { ...d, ...(data.detection || {}), needs_service_type: true } : d,
          ),
        );
        return;
      }
      if (data?.awaiting_webhook) {
        // Aceite enviado ao portal; o acionamento só vira "aceito" (e ganha a
        // OS) quando o webhook de aceite chegar — o cartão fica aguardando.
        toast.success(
          motorista
            ? "Aceite enviado ao portal. O motorista será acionado assim que a OS for criada."
            : "Aceite enviado ao portal. Aguardando a confirmação para criar a OS.",
        );
        setFila((f) =>
          f.map((d) =>
            d.id === det.id
              ? { ...d, ...(data.detection || {}), awaiting_webhook: true, pode_decidir: false }
              : d,
          ),
        );
        return;
      }
      if (!data?.auto_dispatch) toast.success("Acionamento aceito. OS criada e aguardando despacho.");
      // O aviso continua na tela mostrando o link da OS: assim o operador
      // decide se abre o serviço ou segue na fila, sem ser arrastado de tela.
      setFila((f) =>
        f.map((d) =>
          d.id === det.id
            ? {
                ...d,
                ...(data.detection || {}),
                service_id: data.service?.id,
                service_protocolo:
                  data.service?.protocolo_externo || data.service?.protocolo_interno || "",
                needs_service_type: false,
                pode_decidir: false,
              }
            : d,
        ),
      );
    } catch (e) {
      if (e?.response?.status === 410) {
        toast.warning("Este serviço não está mais disponível para aceite.");
        setFila((f) => f.map((d) => (d.id === det.id ? { ...d, indisponivel_aceite: true, pode_decidir: false } : d)));
        fecharComAnimacao(det.id, FECHAR_ACEITO_MS);
        return;
      }
      toast.error(formatApiError(e, "Não foi possível aceitar."));
    } finally {
      setSalvando("");
    }
  };

  const definirTipo = async (det, serviceTypeId) => {
    setSalvando(det.id);
    try {
      const { data } = await api.post(`/portals/detections/${det.id}/service-type`, {
        service_type_id: serviceTypeId,
      });
      aceitosAqui.current.add(det.id);
      avisarAcionamento(data?.auto_dispatch);
      toast.success(
        data?.sugestao_registrada
          ? "OS criada. Sua escolha foi enviada como sugestão de de-para."
          : "OS criada e aguardando despacho.",
      );
      // Aviso segue na tela com o link da OS recém-criada.
      setFila((f) =>
        f.map((d) =>
          d.id === det.id
            ? {
                ...d,
                ...(data.detection || {}),
                service_id: data.service?.id,
                service_protocolo:
                  data.service?.protocolo_externo || data.service?.protocolo_interno || "",
                needs_service_type: false,
                pode_decidir: false,
              }
            : d,
        ),
      );
    } catch (e) {
      toast.error(formatApiError(e, "Não foi possível criar a OS."));
    } finally {
      setSalvando("");
    }
  };

  const recusar = async (det, motivo, obs) => {
    if (!motivo) {
      toast.error("Escolha o motivo da recusa.");
      return;
    }
    setSalvando(det.id);
    try {
      const { data } = await api.post(`/portals/detections/${det.id}/reject`, {
        rejection_code: motivo,
        rejection_notes: obs,
        rejection_description: motivos.find((m) => String(m.code) === String(motivo))?.description || "",
      });
      toast.success("Acionamento recusado no portal.");
      // O cartão vira "Recusado" (como nas outras telas) e sai sozinho.
      setFila((f) => f.map((d) => (d.id === det.id ? { ...d, ...(data?.detection || {}), status: "rejected" } : d)));
      fecharComAnimacao(det.id, FECHAR_ACEITO_MS);
    } catch (e) {
      toast.error(formatApiError(e, "Não foi possível recusar."));
    } finally {
      setSalvando("");
    }
  };

  const dispensar = async (det) => {
    remover(det.id);
    if (det.teste) {
      // Simulação não tem decisão no portal: encerra o aviso da Central para o
      // som parar junto com o cartão.
      await api
        .post("/notifications/resolve", { event_key: EVENTO, entity_id: det.id })
        .catch(() => {});
      return;
    }
    try {
      // Fecha só para este operador: o acionamento continua na fila dos colegas.
      await api.post(`/portals/detections/${det.id}/dismiss`);
    } catch {
      /* sair da fila local já resolve para este operador */
    }
  };

  // Prazo do portal venceu: tira o cartão e encerra o aviso na Central para o
  // som persistente não continuar tocando por um chamado que já morreu.
  const expirar = async (det) => {
    remover(det.id);
    try {
      await api.post("/notifications/resolve", {
        event_key: EVENTO,
        entity_id: det.id,
      });
    } catch {
      /* o cartão já saiu da tela */
    }
  };

  if (!ligado || !fila.length) return null;

  return (
    <div
      // Os cartões ficam acima de tudo (aceitar é urgente), então com um drawer
      // aberto eles vão para a esquerda: à direita cobriam Salvar/Cancelar.
      className={`fixed bottom-4 ${modalAberto ? "left-4 items-start" : "right-4 items-end"} z-[1600] flex flex-col gap-2 max-h-[calc(100vh-96px)] overflow-y-auto pr-1 pointer-events-auto transition-all`}
      data-testid="portal-detection-stack"
      data-aviso-layer="true"
    >
      <div className="sticky top-0 z-10 shrink-0 flex items-center gap-2 rounded-full bg-gray-900 text-white pl-3 pr-1.5 py-1 shadow-lg">
        <span className="text-[11px] font-bold tabular-nums" data-testid="portal-queue-count">
          {fila.length} acionamento{fila.length > 1 ? "s" : ""} aguardando
        </span>
        <Link
          to="/settings/notifications"
          className="h-6 w-6 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          title="Configurar som, push e aviso deste acionamento"
          data-testid="portal-notification-settings-link"
        >
          <SlidersHorizontal size={13} />
        </Link>
      </div>

      {[...fila]
        .reverse()
        .slice(0, visiveis)
        .map((det) => (
          <PortalDetectionCard
            key={det.id}
            det={det}
            motivos={motivos}
            salvando={salvando === det.id}
            onAceitar={(motorista) => aceitar(det, motorista)}
            onDefinirTipo={(tipo) => definirTipo(det, tipo)}
            onRecusar={(motivo, obs) => recusar(det, motivo, obs)}
            onDispensar={() => dispensar(det)}
            onExpirar={() => expirar(det)}
            onPedirMotivos={carregarMotivos}
          />
        ))}
      {fila.length > visiveis && (
        <p
          className="shrink-0 text-[11px] font-semibold text-gray-500 bg-white border border-gray-200 rounded-full px-3 py-1 shadow"
          data-testid="portal-queue-rest"
        >
          +{fila.length - visiveis} na fila — decida os de cima para ver os próximos
        </p>
      )}
    </div>
  );
}
